/**
 * @file: cubeTree 基础查询
 * @author: xiaolong.huang@dmall.com
 */

import * as Utils from '../utils/util';
import { __get__, __getAllCubeIds__, __has__, __getByType__, __getCubeReader__ } from './db';

/**
 * 通过cubeId查询cube
 */
export function findByCubeId(cubeId: CubeId): CubeNode|null {
    return __get__(cubeId) || null;
}

/**
 * 通过多个cubeId查询多个cube
 */
export function findByCubeIds(cubeIds: Array<CubeId>): Array<CubeNode> {
    let result: Array<CubeNode> = [];

    cubeIds.forEach((cubeId) => {
        let cube: CubeNode = findByCubeId(cubeId);

        if (!cube) {
            return;
        }

        result.push(cube);
    });

    return result;
}

export function has(cubeId: CubeId): boolean {
    return __has__(cubeId);
}

/**
 * 获取兄弟节点, 包含自身
 */
export function findBrothersContainSelfByCubeId(cubeId: CubeId): Array<CubeId> {
    let parentId: CubeId = findParentIdByCubeId(cubeId);
    let childCubeIds: Array<CubeId> = getChildCubeIds(parentId);

    return childCubeIds;
}

/**
 * 通过cubeId查询其父级cube
 *
 * TODO: 废弃此接口
 */
export function findParentCubeByCubeId(cubeId: CubeId): CubeNode|null {
    let parentId: string = findParentIdByCubeId(cubeId);

    // 判断是否是空字符串
    if (!Utils.isNotEmptyString(parentId)) {
        return null;
    }

    return __get__(parentId) || null;
}

/**
 * 通过cubeId查询其父级id
 */
export function findParentIdByCubeId(cubeId: CubeId): CubeId|null {
    let cube: CubeNode = __get__(cubeId) || null;

    // 如果当前cube不存在，直接返回null
    if (cube === null) {
        return null;
    }

    // 判断是否是空字符串
    if (!Utils.isNotEmptyString(cube.parentId)) {
        return null;
    }

    return cube.parentId;
}

// 获取cube子cube列表，列表元素为cubeId
export function getChildCubeIds(cubeId: CubeId): Array<CubeId> {
    let parentCube: CubeNode = findByCubeId(cubeId);
    let childCubes: Array<CubeNode> = parentCube.cubes;
    let result: Array<CubeId> = childCubes.map((cube: CubeNode) => {
        return cube.id;
    });

    return result;
}

// 获取cube子cube列表，列表元素为cubeId
export function findChildCubeIds(cubeId: CubeId): Array<CubeId> {
    return getChildCubeIds(cubeId);
}

export function findChildCubeIdsWithSkip(cubeId: CubeId, skip: CubeId[] = []): CubeId[] {
    let result: Set<CubeId> = new Set();
    let skipList: Set<CubeId> = new Set(skip);
    let parentCube: CubeNode = findByCubeId(cubeId);
    let childCubes: Array<CubeNode> = parentCube.cubes;

    childCubes.forEach((cube: CubeNode): void => {
        if (skipList.has(cube.id)) {
            return;
        }
        result.add(cube.id);
    });

    return Array.from(result);
}

// 获取cube的子级字段引用
export function findCubeChildKey(cubeId: CubeId): Array<CubeNode> {
    let cube: CubeNode = findByCubeId(cubeId);

    return cube.cubes;
}

/**
 * 根据索引查找cube
 */
export function findChildCubeByIndex(parentCubeId: CubeId, index: number): CubeId|null {
    let parentCube: CubeNode = findByCubeId(parentCubeId);

    if (!parentCube.cubes
        || parentCube.cubes.length <= 0
        || index > parentCube.cubes.length - 1
        || index < 0) {
        return null;
    }

    return parentCube.cubes[index].id;
}

/**
 * 获取指定类型的cube，注意: cube顺序不可信, 待废弃
 */
export function getCubeByType(cubeType: CubeType): Array<CubeId> {
    return __getByType__(cubeType);
}

export function findCubeByType(cubeType: CubeType): CubeId[] {
    return getCubeByType(cubeType);
}

// 获取所有cube
export function getAllCube(): Array<CubeId> {
    return __getAllCubeIds__();
}

export function findCubeReader(cubeId: CubeId): CubeReader|null {
    return __getCubeReader__(cubeId);
}

/**
 * 计算cube所在父级的索引
 */
export function getCubeIndexInParent(cubeId: CubeId): number {
    let parentCube: CubeNode = findParentCubeByCubeId(cubeId);

    return parentCube.cubes.findIndex(function(cur) {
        return cur.id === cubeId;
    });
}
